import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { auth } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { FiSearch, FiEye, FiPlus, FiFile, FiDownload, FiAlertCircle, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
    fetchCompanyId,
    fetchInventoryItems,
    createInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
    type InventoryItem,
    type ItemFormData
} from '../service/itemsService';
import Table, { Column } from '../../../../components/Table';


export default function Inventory() {
    const pso = useAppSelector((s) => s.user.pso);
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [items, setItems] = useState<InventoryItem[]>([]);
    const [filteredItems, setFilteredItems] = useState<InventoryItem[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    const [showAddModal, setShowAddModal] = useState(false);

    // Check if user can create/edit/delete items
    // Only Incharge users can add/edit/delete items, PSO can only view
    const designation = pso?.designation || '';
    const isIncharge = /incharge/i.test(designation);
    const canManageItems = isIncharge; // Only Incharge users can manage items

    // Determine category from URL or user role
    const inchargeType = designation.replace(/_/g, ' ').toLowerCase();
    const isInchargeCCTV = inchargeType.includes('cctv');
    const isInchargeBAMS = inchargeType.includes('bams');

    // Get category from URL search params or determine from route/user role
    const categoryFromUrl = searchParams.get('category')?.toUpperCase() ||
        (location.pathname.includes('inventory-cctv') ? 'CCTV' :
            location.pathname.includes('inventory-bams') ? 'BAMS' : null);

    const currentCategory = categoryFromUrl || (isInchargeCCTV ? 'CCTV' : isInchargeBAMS ? 'BAMS' : null);
    const isBAMS = currentCategory === 'BAMS';
    const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedItem, setEditedItem] = useState<Partial<InventoryItem> | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<InventoryItem | null>(null);
    const [loading, setLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [categories, setCategories] = useState<string[]>([]);
    const [filterCategory, setFilterCategory] = useState('All');
    const [itemNameOther, setItemNameOther] = useState('');
    const [brandSuggestions, setBrandSuggestions] = useState<string[]>([]);
    const [brandInput, setBrandInput] = useState('');
    const [showBrandSuggestions, setShowBrandSuggestions] = useState(false);
    const [formData, setFormData] = useState<ItemFormData>({
        itemName: '',
        itemDescription: '',
        brandName: 'DAHUA',
        makeModel: '',
        rates: 0,
        taxPercentage: 18,
        itemDate: new Date().toISOString().split('T')[0],
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso' or 'incharge'
    const isBankUser = userRole === 'bank';

    // Close brand suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            setShowBrandSuggestions(false);
        };

        if (showBrandSuggestions) {
            document.addEventListener('click', handleClickOutside);
            return () => {
                document.removeEventListener('click', handleClickOutside);
            };
        }
    }, [showBrandSuggestions]);

    // Fetch companyId
    useEffect(() => {
        if (!uid) return;

        const loadCompanyId = async () => {
            try {
                const id = await fetchCompanyId(uid, isBankUser);
                if (id) {
                    setCompanyId(id);
                }
            } catch (err) {
                console.error('Error fetching company ID:', err);
            }
        };

        loadCompanyId();
    }, [uid, isBankUser]);

    // Fetch items
    useEffect(() => {
        if (!companyId) return;

        const loadItems = async () => {
            setLoading(true);
            try {
                const itemsList = await fetchInventoryItems(companyId);

                // Filter by category if specified
                let filteredList = itemsList;
                if (currentCategory) {
                    if (currentCategory === 'CCTV') {
                        // For CCTV: show items with category 'CCTV' or no category (backward compatibility)
                        filteredList = itemsList.filter(item => {
                            const itemCategory = item.category?.toUpperCase();
                            return !itemCategory || itemCategory === 'CCTV';
                        });
                    } else if (currentCategory === 'BAMS') {
                        // For BAMS: only show items with category 'BAMS'
                        filteredList = itemsList.filter(item => {
                            const itemCategory = item.category?.toUpperCase();
                            return itemCategory === 'BAMS';
                        });
                    }
                } else {
                    // If no category specified, show all items
                    filteredList = itemsList;
                }

                setItems(filteredList);
                setFilteredItems(filteredList);

                // Extract unique categories
                const uniqueCategories = Array.from(new Set(itemsList.map(item => item.category).filter((cat): cat is string => Boolean(cat))));
                setCategories(uniqueCategories);

                // Extract unique brand names for suggestions (for BAMS)
                if (isBAMS) {
                    const uniqueBrands = Array.from(new Set(itemsList
                        .filter(item => item.category?.toUpperCase() === 'BAMS' && item.brandName)
                        .map(item => item.brandName as string)
                        .filter((brand): brand is string => Boolean(brand))));
                    setBrandSuggestions(uniqueBrands);
                }
            } catch (err) {
                console.error(err);
                setToastMessage('Failed to load items');
                setToastType('error');
                setShowToast(true);
            } finally {
                setLoading(false);
            }
        };

        loadItems();
    }, [companyId, currentCategory, isBAMS]);

    // Filter items
    useEffect(() => {
        let list = items;

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(item => {
                const itemDescription = (item.itemDescription || item.itemName || '').toString().toLowerCase();
                const itemCode = (item.itemCode || '').toString().toLowerCase();
                const brandName = (item.brandName || '').toString().toLowerCase();
                const makeModel = (item.makeModel || '').toString().toLowerCase();
                return itemDescription.includes(term) || itemCode.includes(term) || brandName.includes(term) || makeModel.includes(term);
            });
        }

        // Apply category filter (only if not already filtered by currentCategory)
        if (filterCategory !== 'All' && !currentCategory) {
            list = list.filter(item => item.category === filterCategory);
        }

        // Apply date filter
        if (dateFilter !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            list = list.filter(item => {
                if (!item.createdAt) return false;

                let itemDate: Date;
                if (item.createdAt.toDate) {
                    itemDate = item.createdAt.toDate();
                } else if (item.createdAt.seconds) {
                    itemDate = new Date(item.createdAt.seconds * 1000);
                } else {
                    itemDate = new Date(item.createdAt);
                }

                const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());

                switch (dateFilter) {
                    case 'daily':
                        return itemDateOnly.getTime() === today.getTime();
                    case 'weekly':
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        return itemDateOnly.getTime() >= weekAgo.getTime();
                    case 'monthly':
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(today.getMonth() - 1);
                        return itemDateOnly.getTime() >= monthAgo.getTime();
                    default:
                        return true;
                }
            });
        }

        setFilteredItems(list);
        setCurrentPage(1); // Reset to first page when filters change
    }, [items, searchTerm, filterCategory, dateFilter, currentCategory]);

    // Calculate pagination
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedItems = filteredItems.slice(startIndex, endIndex);

    // Format date
    const formatDate = (date: any) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleDateString();
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleDateString();
        }
        // Handle string dates (like itemDate)
        if (typeof date === 'string') {
            return new Date(date).toLocaleDateString();
        }
        return new Date(date).toLocaleDateString();
    };

    // Format datetime
    const formatDateTime = (date: any) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleString();
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleString();
        }
        return new Date(date).toLocaleString();
    };

    // Get status badge
    const getStatusBadge = (status?: string) => {
        if (!status) return null;

        const statusLower = status.toLowerCase();
        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (statusLower === 'available' || statusLower === 'active') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (statusLower === 'low stock') {
            bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
        } else if (statusLower === 'out of stock' || statusLower === 'inactive') {
            bgColor = 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {status}
            </span>
        );
    };

    // Export to Excel
    const exportToExcel = () => {
        if (filteredItems.length === 0) {
            setToastMessage('No items to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const worksheetData = filteredItems.map(item => {
            const baseData: any = {
                'Item Code': item.itemCode || 'N/A',
                'Item Name': item.itemName || 'N/A',
                'Description': item.itemDescription || 'N/A',
                'Brand': item.brandName || 'N/A',
                'Make/Model': item.makeModel || 'N/A',
                'Rates (PKR)': item.rates || 0,
                'Date (SLA)': item.itemDate ? formatDate(item.itemDate) : 'N/A',
            };
            return baseData;
        });

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Items');

        const fileName = `Items_Reports_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        setToastMessage('Excel file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    // Export to PDF
    const exportToPDF = () => {
        if (filteredItems.length === 0) {
            setToastMessage('No items to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const doc = new jsPDF();
        let yPosition = 20;

        // Professional Header
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text('ADVANCE GUARD SECURITY SERVICES', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(16);
        doc.setTextColor(52, 73, 94);
        doc.text('ITEMS REPORTS', 20, yPosition);
        yPosition += 15;

        // Report Information
        doc.setFillColor(248, 249, 250);
        doc.rect(15, yPosition - 5, 180, 35, 'F');
        doc.setDrawColor(189, 195, 199);
        doc.rect(15, yPosition - 5, 180, 35, 'S');

        doc.setFontSize(10);
        doc.setTextColor(52, 73, 94);
        doc.setFont('helvetica', 'bold');
        doc.text('REPORT INFORMATION', 20, yPosition);
        yPosition += 6;

        doc.setFont('helvetica', 'normal');
        doc.text(`Period: ${dateFilter === 'all' ? 'All Time' : dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)}`, 20, yPosition);
        yPosition += 4;
        doc.text(`Total Items: ${filteredItems.length}`, 20, yPosition);
        yPosition += 4;
        doc.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 20, yPosition);
        yPosition += 15;

        // Prepare table data
        const tableData = filteredItems.map(item => {
            return [
                item.itemCode || 'N/A',
                item.itemName || 'N/A',
                item.itemDescription || 'N/A',
                item.brandName || 'N/A',
                item.makeModel || 'N/A',
                item.rates ? item.rates.toString() : 'N/A',
                item.itemDate ? formatDate(item.itemDate) : 'N/A',
            ];
        });

        // Prepare table headers
        const tableHeaders = ['Item Code', 'Item Name', 'Description', 'Brand', 'Make/Model', 'Rates (PKR)', 'Date (SLA)'];

        // Add table using autoTable
        autoTable(doc, {
            head: [tableHeaders],
            body: tableData,
            startY: yPosition,
            styles: {
                fontSize: 7,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: [52, 152, 219],
                textColor: 255,
                fontStyle: 'bold',
            },
            alternateRowStyles: {
                fillColor: [248, 249, 250],
            },
            margin: { top: yPosition, left: 15, right: 15 },
        });

        const fileName = `Items_Reports_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        setToastMessage('PDF file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    // Handle view details
    const handleViewDetails = (item: InventoryItem) => {
        setSelectedItem(item);
        setEditedItem({ ...item });
        setIsEditing(false);
        setShowDetailsModal(true);
    };

    // Handle edit
    const handleEdit = () => {
        if (selectedItem) {
            setIsEditing(true);
            setEditedItem({ ...selectedItem });
        }
    };

    // Handle cancel edit
    const handleCancelEdit = () => {
        setIsEditing(false);
        if (selectedItem) {
            setEditedItem({ ...selectedItem });
        }
    };

    // Handle save edit
    const handleSaveEdit = async () => {
        if (!companyId || !editedItem || !selectedItem) return;

        // Validate required fields
        if (!editedItem.itemName?.trim() || !editedItem.itemDescription?.trim() || !editedItem.makeModel?.trim()) {
            setToastMessage('Please fill in all required fields');
            setToastType('error');
            setShowToast(true);
            return;
        }

        try {
            await updateInventoryItem(companyId, selectedItem.id, {
                itemName: editedItem.itemName,
                itemDescription: editedItem.itemDescription,
                brandName: editedItem.brandName,
                makeModel: editedItem.makeModel,
                rates: editedItem.rates,
                taxPercentage: editedItem.taxPercentage ?? 18,
                itemDate: editedItem.itemDate,
            });
            setToastMessage('Item updated successfully');
            setToastType('success');
            setShowToast(true);
            setIsEditing(false);

            // Refresh items list
            const itemsList = await fetchInventoryItems(companyId);

            // Filter by category if specified
            let filteredList = itemsList;
            if (currentCategory) {
                if (currentCategory === 'CCTV') {
                    // For CCTV: show items with category 'CCTV' or no category (backward compatibility)
                    filteredList = itemsList.filter(item => {
                        const itemCategory = item.category?.toUpperCase();
                        return !itemCategory || itemCategory === 'CCTV';
                    });
                } else if (currentCategory === 'BAMS') {
                    // For BAMS: only show items with category 'BAMS'
                    filteredList = itemsList.filter(item => item.category?.toUpperCase() === 'BAMS');
                }
            }

            setItems(filteredList);
            const uniqueCategories = Array.from(new Set(itemsList.map(item => item.category).filter((cat): cat is string => Boolean(cat))));
            setCategories(uniqueCategories);
            setSelectedItem(editedItem as InventoryItem);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to update item');
            setToastType('error');
            setShowToast(true);
        }
    };

    // Handle delete button click - open confirmation modal
    const handleDeleteClick = (item: InventoryItem) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
    };

    // Handle delete confirmation
    const handleDeleteConfirm = async () => {
        if (!companyId || !itemToDelete) {
            return;
        }

        setDeleting(true);
        try {
            await deleteInventoryItem(companyId, itemToDelete.id);

            setToastMessage('Item deleted successfully');
            setToastType('success');
            setShowToast(true);

            // Refresh items list
            const itemsList = await fetchInventoryItems(companyId);

            // Filter by category if specified
            let filteredList = itemsList;
            if (currentCategory) {
                if (currentCategory === 'CCTV') {
                    // For CCTV: show items with category 'CCTV' or no category (backward compatibility)
                    filteredList = itemsList.filter(item => {
                        const itemCategory = item.category?.toUpperCase();
                        return !itemCategory || itemCategory === 'CCTV';
                    });
                } else if (currentCategory === 'BAMS') {
                    // For BAMS: only show items with category 'BAMS'
                    filteredList = itemsList.filter(item => item.category?.toUpperCase() === 'BAMS');
                }
            }

            setItems(filteredList);
            const uniqueCategories = Array.from(new Set(itemsList.map(item => item.category).filter((cat): cat is string => Boolean(cat))));
            setCategories(uniqueCategories);

            // Close modal if the deleted item was selected
            if (selectedItem?.id === itemToDelete.id) {
                setShowDetailsModal(false);
                setSelectedItem(null);
            }

            // Close delete confirmation modal
            setShowDeleteModal(false);
            setItemToDelete(null);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to delete item');
            setToastType('error');
            setShowToast(true);
        } finally {
            setDeleting(false);
        }
    };

    // Handle delete cancel
    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setItemToDelete(null);
    };

    // Generate Item Code
    const generateItemCode = (brandName: string, category?: string): string => {
        if (category === 'BAMS') {
            // For BAMS, use first 3 letters of brand name or 'BAM' if no brand name
            const prefix = brandName && brandName.trim() ? brandName.substring(0, 3).toUpperCase() : 'BAM';
            const existingItems = items.filter(item => {
                if (item.category !== 'BAMS') return false;
                if (!item.brandName || !item.brandName.trim()) {
                    return prefix === 'BAM';
                }
                return item.brandName.toUpperCase().substring(0, 3) === prefix;
            });
            const nextNumber = existingItems.length + 1;
            return `${prefix}${nextNumber.toString().padStart(4, '0')}`;
        } else {
            // For CCTV, use D or H prefix
            const prefix = brandName === 'DAHUA' ? 'D' : 'H';
            const existingItems = items.filter(item =>
                item.category !== 'BAMS' &&
                item.brandName === brandName
            );
            const nextNumber = existingItems.length + 1;
            return `${prefix}${nextNumber.toString().padStart(4, '0')}`;
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            itemName: '',
            itemDescription: '',
            brandName: isBAMS ? '' : 'DAHUA',
            makeModel: '',
            rates: 0,
            taxPercentage: 18,
            itemDate: new Date().toISOString().split('T')[0],
        });
        setItemNameOther('');
        setBrandInput('');
        setShowBrandSuggestions(false);
    };

    // Handle form change
    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (name === 'itemName') {
            if (value === 'Other') {
                setFormData(prev => ({
                    ...prev,
                    itemName: itemNameOther || 'Other'
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    itemName: value
                }));
                setItemNameOther('');
            }
        } else if (name === 'brandName' && isBAMS) {
            // For BAMS, update both brandInput and formData
            setBrandInput(value);
            setFormData(prev => ({
                ...prev,
                brandName: value as any
            }));
            setShowBrandSuggestions(true);
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: name === 'rates' || name === 'taxPercentage' ? parseInt(value, 10) || 0 : value
            }));
        }
    };

    // Handle brand input change for BAMS
    const handleBrandInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setBrandInput(value);
        setFormData(prev => ({
            ...prev,
            brandName: value as any
        }));
        setShowBrandSuggestions(true);
    };

    // Handle brand suggestion selection
    const handleBrandSuggestionSelect = (brand: string) => {
        setBrandInput(brand);
        setFormData(prev => ({
            ...prev,
            brandName: brand as any
        }));
        setShowBrandSuggestions(false);
    };

    // Handle item name other change
    const handleItemNameOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setItemNameOther(value);
        setFormData(prev => ({
            ...prev,
            itemName: value
        }));
    };

    // Handle add item
    const handleAddItem = async () => {
        if (!companyId || !uid) {
            setToastMessage('Company ID or User ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        // For BAMS items, brand name and make/model are optional
        if (isBAMS) {
            if (!formData.itemName.trim() || !formData.itemDescription.trim()) {
                setToastMessage('Please fill in all required fields');
                setToastType('error');
                setShowToast(true);
                return;
            }
        } else {
            // For CCTV items, all fields are required
            if (!formData.itemName.trim() || !formData.itemDescription.trim() || !formData.makeModel.trim() || !formData.brandName.trim()) {
                setToastMessage('Please fill in all required fields');
                setToastType('error');
                setShowToast(true);
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const itemCode = generateItemCode(formData.brandName, currentCategory || undefined);
            const itemData = {
                ...formData,
                itemCode,
                category: currentCategory || undefined,
                taxPercentage: formData.taxPercentage || 18,
            };

            await createInventoryItem(companyId, itemData);

            // If brand is new, add to suggestions
            if (isBAMS && formData.brandName && !brandSuggestions.includes(formData.brandName)) {
                setBrandSuggestions([...brandSuggestions, formData.brandName]);
            }

            setToastMessage('Item added successfully');
            setToastType('success');
            setShowToast(true);
            setShowAddModal(false);
            resetForm();

            // Refresh items list
            const itemsList = await fetchInventoryItems(companyId);

            // Filter by category if specified
            let filteredList = itemsList;
            if (currentCategory) {
                if (currentCategory === 'CCTV') {
                    // For CCTV: show items with category 'CCTV' or no category (backward compatibility)
                    filteredList = itemsList.filter(item => {
                        const itemCategory = item.category?.toUpperCase();
                        return !itemCategory || itemCategory === 'CCTV';
                    });
                } else if (currentCategory === 'BAMS') {
                    // For BAMS: only show items with category 'BAMS'
                    filteredList = itemsList.filter(item => item.category?.toUpperCase() === 'BAMS');
                }
            }

            setItems(filteredList);
            const uniqueCategories = Array.from(new Set(itemsList.map(item => item.category).filter((cat): cat is string => Boolean(cat))));
            setCategories(uniqueCategories);

            // Update brand suggestions for BAMS
            if (isBAMS) {
                const uniqueBrands = Array.from(new Set(itemsList
                    .filter(item => item.category?.toUpperCase() === 'BAMS' && item.brandName)
                    .map(item => item.brandName as string)
                    .filter((brand): brand is string => Boolean(brand))));
                setBrandSuggestions(uniqueBrands);
            }
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to add item');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const columns: Column<InventoryItem>[] = [
        {
            header: 'Item Code',
            accessor: 'itemCode',
            width: '12%',
            render: (item: InventoryItem) => (
                <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                    {item.itemCode || 'N/A'}
                </span>
            )
        },
        { header: 'Item Name', accessor: 'itemName', width: '15%', render: (item: InventoryItem) => <span className="text-gray-900 dark:text-gray-100">{item.itemName || 'N/A'}</span> },
        {
            header: 'Description',
            accessor: 'itemDescription',
            width: '20%',
            render: (item: InventoryItem) => (
                <div
                    className="truncate max-w-[200px]"
                    title={item.itemDescription || 'N/A'}
                >
                    {item.itemDescription || 'N/A'}
                </div>
            )
        },
        {
            header: 'Brand',
            accessor: 'brandName',
            width: '10%',
            render: (item: InventoryItem) => item.brandName ? (
                <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${item.brandName === 'DAHUA'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                    : item.brandName === 'HIKVISION'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                        : 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
                    }`}>
                    {item.brandName}
                </span>
            ) : 'N/A'
        },
        { header: 'Make/Model', accessor: 'makeModel', width: '10%', render: (item: InventoryItem) => <span className="text-gray-900 dark:text-gray-100">{item.makeModel || 'N/A'}</span> },
    ];

    columns.push(
        {
            header: 'Before Tax Price',
            width: '10%',
            render: (item) => {
                const rates = item.rates || 0;
                const taxPercentage = item.taxPercentage ?? 18;
                const taxRate = taxPercentage / 100;
                const beforeTaxPrice = rates > 0 ? rates / (1 + taxRate) : 0;
                return rates > 0 ? `${Math.round(beforeTaxPrice)} PKR` : 'N/A';
            }
        },
        {
            header: 'Rates (PKR)',
            accessor: 'rates',
            width: '10%',
            render: (item: InventoryItem) => <span className="font-semibold text-gray-900 dark:text-gray-100">{item.rates ? `${item.rates} PKR` : 'N/A'}</span>
        },
        {
            header: 'Tax %',
            accessor: 'taxPercentage',
            width: '8%',
            render: (item: InventoryItem) => <span>{item.taxPercentage ?? 18}%</span>
        }
    );

    columns.push(
        { header: 'Date (SLA)', accessor: 'itemDate', width: '10%', render: (item: InventoryItem) => item.itemDate ? formatDate(item.itemDate) : 'N/A' },
        {
            header: 'Actions',
            align: 'right',
            render: (item: InventoryItem) => (
                <div className="flex justify-end gap-2">
                    <button
                        onClick={() => handleViewDetails(item)}
                        className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-5 w-5" />
                    </button>
                    {canManageItems && (
                        <button
                            onClick={() => handleDeleteClick(item)}
                            disabled={deleting}
                            className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            title="Delete Item"
                        >
                            <Icon icon={FiTrash2} className="h-5 w-5" />
                        </button>
                    )}
                </div>
            )
        }
    );

    const headerActions = (
        <div className="flex flex-wrap gap-3">
            {canManageItems && (
                <Button variant="primary" onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
                    <Icon icon={FiPlus} className="h-4 w-4" />
                    Add Items
                </Button>
            )}
            {!canManageItems && (
                <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                    <Icon icon={FiAlertCircle} className="h-4 w-4" />
                    View Only - Only Incharge can add items
                </div>
            )}
            <Button variant="secondary" onClick={exportToExcel} className="flex items-center gap-2">
                <Icon icon={FiFile} className="h-4 w-4" />
                Export Excel
            </Button>
            <Button variant="secondary" onClick={exportToPDF} className="flex items-center gap-2">
                <Icon icon={FiDownload} className="h-4 w-4" />
                Export PDF
            </Button>
        </div>
    );

    const filters = (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                    Search Items
                </label>
                <TextField
                    type="text"
                    placeholder="Search by item code, name, description, brand, or make/model..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    prefix={<Icon icon={FiSearch} />}
                />
            </div>
            {!currentCategory && (
                <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Filter by Category
                    </label>
                    <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                    >
                        <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Categories</option>
                        {categories.map((category) => (
                            <option key={category} value={category} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{category}</option>
                        ))}
                    </select>
                </div>
            )}
            <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                    Filter by Date
                </label>
                <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value as 'all' | 'daily' | 'weekly' | 'monthly')}
                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                >
                    <option value="all" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Time</option>
                    <option value="daily" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Daily</option>
                    <option value="weekly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Weekly</option>
                    <option value="monthly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Monthly</option>
                </select>
            </div>
        </div>
    );

    return (
        <>
            <div className="p-6">
                {/* Toast */}
                {showToast && (
                    <div className="pointer-events-none fixed right-4 top-4 z-50">
                        <div className="pointer-events-auto">
                            <Toast
                                message={toastMessage}
                                type={toastType}
                                onClose={() => setShowToast(false)}
                            />
                        </div>
                    </div>
                )}

                {/* Main Table */}
                <Table
                    title={`Manage ${currentCategory ? `${currentCategory} ` : ''}Items`}
                    description="Track and manage items"
                    data={paginatedItems}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage={
                        currentCategory
                            ? `No ${currentCategory} items found. ${canManageItems ? 'Click "Add Items" to create new items.' : 'Please contact an incharge to add items.'}`
                            : 'No items found matching your criteria.'
                    }
                    headerActions={headerActions}
                    filters={filters}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                />
            </div>










            {/* Item Details Modal */}
            {
                showDetailsModal && selectedItem && editedItem && (
                    <Modal
                        isOpen={showDetailsModal}
                        onClose={() => {
                            setShowDetailsModal(false);
                            setSelectedItem(null);
                            setIsEditing(false);
                            setEditedItem(null);
                        }}
                        title={`Item Details - ${selectedItem.itemCode || selectedItem.id}`}
                        size="lg"
                    >
                        <div className="p-6">
                            {/* Edit Button in Header */}
                            {!isEditing && canManageItems && (
                                <div className="mb-4 flex justify-end">
                                    <Button
                                        variant="secondary"
                                        onClick={handleEdit}
                                        className="flex items-center gap-2"
                                    >
                                        <Icon icon={FiEdit2} className="h-4 w-4" />
                                        Edit
                                    </Button>
                                </div>
                            )}

                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Item Code</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                                                {selectedItem.itemCode || 'N/A'}
                                            </span>
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Item Name</label>
                                        {isEditing ? (
                                            <div>
                                                {selectedItem.category === 'BAMS' ? (
                                                    <>
                                                        <select
                                                            value={editedItem.itemName && !['BAMS system', 'Fixed Hand Panic', 'Remote Panic', 'Fixed Foot Panic', 'PIR/ Motion sensor', 'Opening door contact', 'Smoke Detector', 'Hooter/ Sounder', 'Keypad', 'Cables/ Wires'].includes(editedItem.itemName) ? 'Other' : (editedItem.itemName || '')}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (value === 'Other') {
                                                                    setEditedItem({ ...editedItem, itemName: editedItem.itemName && !['BAMS system', 'Fixed Hand Panic', 'Remote Panic', 'Fixed Foot Panic', 'PIR/ Motion sensor', 'Opening door contact', 'Smoke Detector', 'Hooter/ Sounder', 'Keypad', 'Cables/ Wires'].includes(editedItem.itemName) ? editedItem.itemName : '' });
                                                                } else {
                                                                    setEditedItem({ ...editedItem, itemName: value });
                                                                }
                                                            }}
                                                            className="mt-1 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                        >
                                                            <option value="" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Select Item Name</option>
                                                            <option value="BAMS system" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">BAMS system</option>
                                                            <option value="Fixed Hand Panic" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Fixed Hand Panic</option>
                                                            <option value="Remote Panic" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Remote Panic</option>
                                                            <option value="Fixed Foot Panic" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Fixed Foot Panic</option>
                                                            <option value="PIR/ Motion sensor" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">PIR/ Motion sensor</option>
                                                            <option value="Opening door contact" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Opening door contact</option>
                                                            <option value="Smoke Detector" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Smoke Detector</option>
                                                            <option value="Hooter/ Sounder" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Hooter/ Sounder</option>
                                                            <option value="Keypad" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Keypad</option>
                                                            <option value="Cables/ Wires" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Cables/ Wires</option>
                                                            <option value="Other" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Other</option>
                                                        </select>
                                                        {(editedItem.itemName === 'Other' || (editedItem.itemName && !['BAMS system', 'Fixed Hand Panic', 'Remote Panic', 'Fixed Foot Panic', 'PIR/ Motion sensor', 'Opening door contact', 'Smoke Detector', 'Hooter/ Sounder', 'Keypad', 'Cables/ Wires'].includes(editedItem.itemName))) && (
                                                            <TextField
                                                                type="text"
                                                                value={editedItem.itemName === 'Other' ? '' : (editedItem.itemName || '')}
                                                                onChange={(e) => setEditedItem({ ...editedItem, itemName: e.target.value })}
                                                                placeholder="Enter item name"
                                                                className="mt-2"
                                                                required
                                                            />
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <select
                                                            value={editedItem.itemName && !['Camera', 'DVR/NVR', 'Switches', 'CCTV Display', 'Hard DISK', 'Racks', 'Cable Manager and Cabling', 'Piping'].includes(editedItem.itemName) ? 'Other' : (editedItem.itemName || '')}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (value === 'Other') {
                                                                    setEditedItem({ ...editedItem, itemName: editedItem.itemName && !['Camera', 'DVR/NVR', 'Switches', 'CCTV Display', 'Hard DISK', 'Racks', 'Cable Manager and Cabling', 'Piping'].includes(editedItem.itemName) ? editedItem.itemName : '' });
                                                                } else {
                                                                    setEditedItem({ ...editedItem, itemName: value });
                                                                }
                                                            }}
                                                            className="mt-1 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                        >
                                                            <option value="" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Select Item Name</option>
                                                            <option value="Camera" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Camera</option>
                                                            <option value="DVR/NVR" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">DVR/NVR</option>
                                                            <option value="Switches" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Switches</option>
                                                            <option value="CCTV Display" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">CCTV Display</option>
                                                            <option value="Hard DISK" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Hard DISK</option>
                                                            <option value="Racks" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Racks</option>
                                                            <option value="Cable Manager and Cabling" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Cable Manager and Cabling</option>
                                                            <option value="Piping" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Piping</option>
                                                            <option value="Other" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Other</option>
                                                        </select>
                                                        {(editedItem.itemName === 'Other' || (editedItem.itemName && !['Camera', 'DVR/NVR', 'Switches', 'CCTV Display', 'Hard DISK', 'Racks', 'Cable Manager and Cabling', 'Piping'].includes(editedItem.itemName))) && (
                                                            <TextField
                                                                type="text"
                                                                value={editedItem.itemName === 'Other' ? '' : (editedItem.itemName || '')}
                                                                onChange={(e) => setEditedItem({ ...editedItem, itemName: e.target.value })}
                                                                placeholder="Enter item name"
                                                                className="mt-2"
                                                                required
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedItem.itemName || 'N/A'}</p>
                                        )}
                                    </div>
                                    <div className="relative">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Brand Name</label>
                                        {isEditing ? (
                                            selectedItem.category === 'BAMS' ? (
                                                <TextField
                                                    type="text"
                                                    value={editedItem.brandName || ''}
                                                    onChange={(e) => setEditedItem({ ...editedItem, brandName: e.target.value })}
                                                    className="mt-1"
                                                    required
                                                />
                                            ) : (
                                                <select
                                                    value={editedItem.brandName || ''}
                                                    onChange={(e) => setEditedItem({ ...editedItem, brandName: e.target.value })}
                                                    className="mt-1 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                >
                                                    <option value="DAHUA" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">DAHUA</option>
                                                    <option value="HIKVISION" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">HIKVISION</option>
                                                </select>
                                            )
                                        ) : (
                                            <p className="mt-1">
                                                {selectedItem.brandName ? (
                                                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${selectedItem.brandName === 'DAHUA'
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                                                        : selectedItem.brandName === 'HIKVISION'
                                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                                                            : 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
                                                        }`}>
                                                        {selectedItem.brandName}
                                                    </span>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Item Description</label>
                                        {isEditing ? (
                                            <TextField
                                                type="text"
                                                value={editedItem.itemDescription || ''}
                                                onChange={(e) => setEditedItem({ ...editedItem, itemDescription: e.target.value })}
                                                className="mt-1"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedItem.itemDescription || 'N/A'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Make / Model</label>
                                        {isEditing ? (
                                            <TextField
                                                type="text"
                                                value={editedItem.makeModel || ''}
                                                onChange={(e) => setEditedItem({ ...editedItem, makeModel: e.target.value })}
                                                className="mt-1"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedItem.makeModel || 'N/A'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Rates in PKR (Incl. taxes)</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedItem.rates || 0}
                                                onChange={(e) => setEditedItem({ ...editedItem, rates: parseInt(e.target.value, 10) || 0 })}
                                                className="mt-1"
                                                step="1"
                                                min="0"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {selectedItem.rates ? `${selectedItem.rates} PKR` : 'N/A'}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Tax Percentage (%)</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedItem.taxPercentage ?? 18}
                                                onChange={(e) => setEditedItem({ ...editedItem, taxPercentage: parseInt(e.target.value, 10) || 18 })}
                                                className="mt-1"
                                                step="1"
                                                min="0"
                                                max="100"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                {selectedItem.taxPercentage ?? 18}%
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date (SLA)</label>
                                        {isEditing ? (
                                            <TextField
                                                type="date"
                                                value={editedItem.itemDate || ''}
                                                onChange={(e) => setEditedItem({ ...editedItem, itemDate: e.target.value })}
                                                className="mt-1"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                {selectedItem.itemDate ? formatDate(selectedItem.itemDate) : 'N/A'}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Added Item At</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedItem.createdAt)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                <Button variant="secondary" onClick={() => {
                                    setShowDetailsModal(false);
                                    setSelectedItem(null);
                                    setIsEditing(false);
                                    setEditedItem(null);
                                }}>
                                    {isEditing ? 'Cancel' : 'Close'}
                                </Button>
                                {isEditing && (
                                    <>
                                        <Button
                                            variant="secondary"
                                            onClick={handleCancelEdit}
                                        >
                                            <Icon icon={FiX} className="mr-1 h-4 w-4" />
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleSaveEdit}
                                        >
                                            <Icon icon={FiEdit2} className="mr-1 h-4 w-4" />
                                            Save Changes
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </Modal>
                )
            }

            {/* Add Items Modal */}
            {
                showAddModal && (
                    <Modal
                        isOpen={showAddModal}
                        onClose={() => {
                            setShowAddModal(false);
                            resetForm();
                        }}
                        title="Add New Item"
                        size="lg"
                    >
                        <div className="p-6">
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                handleAddItem();
                            }}>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Item Name <span className="text-red-500">*</span>
                                            </label>
                                            {isBAMS ? (
                                                <>
                                                    <select
                                                        name="itemName"
                                                        value={formData.itemName && !['BAMS system', 'Fixed Hand Panic', 'Remote Panic', 'Fixed Foot Panic', 'PIR/ Motion sensor', 'Opening door contact', 'Smoke Detector', 'Hooter/ Sounder', 'Keypad', 'Cables/ Wires'].includes(formData.itemName) ? 'Other' : (formData.itemName || '')}
                                                        onChange={handleFormChange}
                                                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                        required
                                                    >
                                                        <option value="" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Select Item Name</option>
                                                        <option value="BAMS system" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">BAMS system</option>
                                                        <option value="Fixed Hand Panic" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Fixed Hand Panic</option>
                                                        <option value="Remote Panic" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Remote Panic</option>
                                                        <option value="Fixed Foot Panic" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Fixed Foot Panic</option>
                                                        <option value="PIR/ Motion sensor" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">PIR/ Motion sensor</option>
                                                        <option value="Opening door contact" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Opening door contact</option>
                                                        <option value="Smoke Detector" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Smoke Detector</option>
                                                        <option value="Hooter/ Sounder" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Hooter/ Sounder</option>
                                                        <option value="Keypad" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Keypad</option>
                                                        <option value="Cables/ Wires" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Cables/ Wires</option>
                                                        <option value="Other" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Other</option>
                                                    </select>
                                                    {(formData.itemName === 'Other' || (formData.itemName && !['BAMS system', 'Fixed Hand Panic', 'Remote Panic', 'Fixed Foot Panic', 'PIR/ Motion sensor', 'Opening door contact', 'Smoke Detector', 'Hooter/ Sounder', 'Keypad', 'Cables/ Wires'].includes(formData.itemName))) && (
                                                        <TextField
                                                            type="text"
                                                            value={formData.itemName && !['BAMS system', 'Fixed Hand Panic', 'Remote Panic', 'Fixed Foot Panic', 'PIR/ Motion sensor', 'Opening door contact', 'Smoke Detector', 'Hooter/ Sounder', 'Keypad', 'Cables/ Wires'].includes(formData.itemName) ? formData.itemName : itemNameOther}
                                                            onChange={handleItemNameOtherChange}
                                                            placeholder="Enter item name"
                                                            className="mt-2"
                                                            required
                                                        />
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <select
                                                        name="itemName"
                                                        value={formData.itemName && !['Camera', 'DVR/NVR', 'Switches', 'CCTV Display', 'Hard DISK', 'Racks', 'Cable Manager and Cabling', 'Piping'].includes(formData.itemName) ? 'Other' : (formData.itemName || '')}
                                                        onChange={handleFormChange}
                                                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                        required
                                                    >
                                                        <option value="" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Select Item Name</option>
                                                        <option value="Camera" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Camera</option>
                                                        <option value="DVR/NVR" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">DVR/NVR</option>
                                                        <option value="Switches" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Switches</option>
                                                        <option value="CCTV Display" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">CCTV Display</option>
                                                        <option value="Hard DISK" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Hard DISK</option>
                                                        <option value="Racks" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Racks</option>
                                                        <option value="Cable Manager and Cabling" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Cable Manager and Cabling</option>
                                                        <option value="Piping" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Piping</option>
                                                        <option value="Other" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Other</option>
                                                    </select>
                                                    {(formData.itemName === 'Other' || (formData.itemName && !['Camera', 'DVR/NVR', 'Switches', 'CCTV Display', 'Hard DISK', 'Racks', 'Cable Manager and Cabling', 'Piping'].includes(formData.itemName))) && (
                                                        <TextField
                                                            type="text"
                                                            value={formData.itemName && !['Camera', 'DVR/NVR', 'Switches', 'CCTV Display', 'Hard DISK', 'Racks', 'Cable Manager and Cabling'].includes(formData.itemName) ? formData.itemName : itemNameOther}
                                                            onChange={handleItemNameOtherChange}
                                                            placeholder="Enter item name"
                                                            className="mt-2"
                                                            required
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className="relative">
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Brand Name {!isBAMS && <span className="text-red-500">*</span>}
                                            </label>
                                            {isBAMS ? (
                                                <>
                                                    <TextField
                                                        type="text"
                                                        name="brandName"
                                                        value={brandInput}
                                                        onChange={handleBrandInputChange}
                                                        onFocus={() => setShowBrandSuggestions(true)}
                                                        placeholder="Enter brand name (optional)"
                                                        className="w-full"
                                                    />
                                                    {showBrandSuggestions && brandSuggestions.length > 0 && brandInput && (
                                                        <div
                                                            className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                        >
                                                            {brandSuggestions
                                                                .filter(brand => brand.toLowerCase().includes(brandInput.toLowerCase()))
                                                                .map((brand, index) => (
                                                                    <button
                                                                        key={index}
                                                                        type="button"
                                                                        onClick={() => handleBrandSuggestionSelect(brand)}
                                                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                                                                    >
                                                                        {brand}
                                                                    </button>
                                                                ))}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <select
                                                    name="brandName"
                                                    value={formData.brandName}
                                                    onChange={handleFormChange}
                                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                    required
                                                >
                                                    <option value="DAHUA" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">DAHUA</option>
                                                    <option value="HIKVISION" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">HIKVISION</option>
                                                </select>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Item Description <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="text"
                                                name="itemDescription"
                                                value={formData.itemDescription}
                                                onChange={handleFormChange}
                                                placeholder="Enter item description"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Make / Model {!isBAMS && <span className="text-red-500">*</span>}
                                            </label>
                                            <TextField
                                                type="text"
                                                name="makeModel"
                                                value={formData.makeModel}
                                                onChange={handleFormChange}
                                                placeholder={isBAMS ? "Enter make and model (optional)" : "Enter make and model"}
                                                required={!isBAMS}
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Rates in PKR (Incl. taxes) <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="number"
                                                name="rates"
                                                value={formData.rates}
                                                onChange={handleFormChange}
                                                placeholder="0"
                                                step="1"
                                                min="0"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Tax Percentage (%) <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="number"
                                                name="taxPercentage"
                                                value={formData.taxPercentage}
                                                onChange={handleFormChange}
                                                placeholder="18"
                                                step="1"
                                                min="0"
                                                max="100"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Date (SLA) <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="date"
                                                name="itemDate"
                                                value={formData.itemDate}
                                                onChange={handleFormChange}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                                                <p className="text-xs font-semibold text-blue-900 dark:text-blue-100">
                                                    Auto-generated Item Code
                                                </p>
                                                <p className="mt-1 text-sm font-bold text-blue-700 dark:text-blue-300">
                                                    {generateItemCode(formData.brandName, currentCategory || undefined)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setShowAddModal(false);
                                            resetForm();
                                        }}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                                Adding...
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon={FiPlus} className="mr-1 h-4 w-4" />
                                                Add Item
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </Modal>
                )
            }

            {/* Delete Confirmation Modal */}
            {
                showDeleteModal && itemToDelete && (
                    <Modal
                        isOpen={showDeleteModal}
                        onClose={handleDeleteCancel}
                        title="Confirm Deletion"
                        size="md"
                    >
                        <div className="p-6">
                            <div className="mb-4 text-center">
                                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                                    <Icon icon={FiAlertCircle} className="h-6 w-6 text-red-600 dark:text-red-400" />
                                </div>
                            </div>
                            <div className="text-center">
                                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    Are you sure?
                                </h3>
                                <p className="mb-1 text-sm text-gray-600 dark:text-gray-400">
                                    Do you really want to delete this item?
                                </p>
                                <p className="mb-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                                    <span className="font-semibold">Item Code:</span> {itemToDelete.itemCode || 'N/A'}
                                    <br />
                                    <span className="font-semibold">Item Name:</span> {itemToDelete.itemName || 'N/A'}
                                </p>
                                <p className="text-sm text-red-600 dark:text-red-400">
                                    This action cannot be undone.
                                </p>
                            </div>
                            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                <Button
                                    variant="secondary"
                                    onClick={handleDeleteCancel}
                                    disabled={deleting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleDeleteConfirm}
                                    disabled={deleting}
                                    className="flex items-center gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
                                >
                                    {deleting ? (
                                        <>
                                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Icon icon={FiTrash2} className="h-4 w-4" />
                                            Delete Item
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </Modal>
                )
            }
        </>
    );
}

